/**
 * Parses "Apellidos, Nombres" format handling compound prefixes.
 *
 * Compound prefixes (De, Di, Van, etc.) are joined with the following word
 * to form a single logical name. Works for both surnames and first names
 * (e.g., "De los Ángeles" → one name).
 *
 * Returns up to `max` logical names per side (default 3, matching DNDA schema).
 */

const COMPOUND_PREFIXES = new Set([
  "de", "del", "de la", "de las", "de los",
  "di",
  "da", "das", "do", "dos",
  "du", "le", "la",
  "van", "van de", "van den", "van der",
  "von", "von der",
  "mac", "mc",
  "san", "santa",
  "los", "las", "lo",
]);

// Sorted longest-first so "de la" matches before "de"
const SORTED_PREFIXES = Array.from(COMPOUND_PREFIXES).sort((a, b) => b.length - a.length);

/**
 * Splits a space-separated string into logical names respecting compound prefixes.
 * "Di Benedetto García" → ["Di Benedetto", "García"]
 * "María de los Ángeles" → ["María", "de los Ángeles"]
 */
function splitWithCompoundPrefixes(text: string, max: number): string[] {
  const words = text.trim().split(/\s+/);
  const result: string[] = [];
  let i = 0;

  while (i < words.length && result.length < max) {
    const remaining = words.slice(i).join(" ").toLowerCase();

    let matched = false;
    for (const prefix of SORTED_PREFIXES) {
      if (remaining.startsWith(prefix + " ") && i + prefix.split(/\s+/).length < words.length) {
        const prefixWordCount = prefix.split(/\s+/).length;
        const compoundParts = words.slice(i, i + prefixWordCount + 1);
        result.push(compoundParts.join(" "));
        i += prefixWordCount + 1;
        matched = true;
        break;
      }
    }

    if (!matched) {
      result.push(words[i]);
      i++;
    }
  }

  // If there are remaining words and we hit max, append them to the last entry
  if (i < words.length && result.length === max) {
    const leftover = words.slice(i).join(" ");
    result[max - 1] += " " + leftover;
  }

  return result;
}

export interface ParsedPersonName {
  apellidos: string[];
  nombres: string[];
}

export function parsePersonName(input: string, max = 3): ParsedPersonName | null {
  const commaIdx = input.indexOf(",");
  if (commaIdx <= 0) return null;

  const apellidosPart = input.substring(0, commaIdx).trim();
  const nombresPart = input.substring(commaIdx + 1).trim();

  if (!apellidosPart || !nombresPart) return null;

  return {
    apellidos: splitWithCompoundPrefixes(apellidosPart, max),
    nombres: splitWithCompoundPrefixes(nombresPart, max),
  };
}
